import axios from "axios";
import { newLogger } from "./util";
import LoadingMixin from "@/mixins/LoadingMixin";

let logger = newLogger("Network");

let AUTH_HEADER = "X-Auth-Token";
let VERSION_HEADER = "X-Version";

let delay = (t) => {
  return new Promise((resolve) => {
    setTimeout(resolve, t);
  });
};

class Network {
  constructor() {
    this.router = null;
    this.store = null;
    this.http = null;
    this.excelNetwork = null;
    this.pdfNetwork = null;
  }

  /**
   * Called from app.vue on creation, sets the store reference and creates the axios instance
   */
  configure(store, router, toast) {
    this.router = router;
    this.store = store;
    this.http = this.createAxiosInstance();
    this.excelNetwork = this.createExcelExportNetwork();
    this.pdfNetwork = this.createPdfExportNetwork();
    this.toast = toast;
  }

  getJwt() {
    return this.store.getters["session/jwt"];
  }

  getVersion() {
    const env = this.store.getters["env/getField"];
    return env("CLIENT_VERSION");
  }

  createUnauthorizedAxiosInstance() {
    return axios.create({
      baseURL: "/api",
      timeout: 30000,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      // responseType: 'json',
    });
  }

  createAxiosInstance() {
    const http = axios.create({
      baseURL: "/api",
      timeout: 60000,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      // responseType: 'json',
    });
    http.interceptors.request.use(
      (config) => {
        let jwt = this.getJwt();
        if (!jwt || jwt === "") {
          throw "No JWT to use!";
        }
        config.headers[AUTH_HEADER] = jwt;
        config.headers[VERSION_HEADER] = this.getVersion();
        // logger.debug("Added JWT to outgoing call.");
        return config;
      },
      (error) => {
        logger.error("AJAX call wasn't even sent.");
        logger.error(error);
        return Promise.reject(error);
      }
    );
    http.interceptors.response.use(
      (response) => {
        this.checkRefresh(response);
        let jwt = response.headers[AUTH_HEADER.toLowerCase()];
        if (jwt) {
          logger.debug("Refreshing JWT...");
          this.store.dispatch("session/refreshJwt", jwt);
        } else {
          // logger.debug("No JWT in response!");
        }
        return Promise.resolve(response);
      },
      (error) => {
        logger.warn("AJAX call resulted in an error!");
        logger.warn(error);
        if (error.response.status === 401) {
          // unauthorized
          this.store.dispatch("session/boot");
          if (
            this.router.currentRoute.name !== "SignIn" &&
            this.router.currentRoute.name !== "SignInToTenant"
          )
            this.router.push({
              path: "/login",
              query: { returnUrl: this.router.currentRoute.fullPath },
            });
          LoadingMixin.methods.doFinishLoading().then(() => {});
        } else {
          this.toast.add({
            severity: "error",
            summary: "Something went wrong.., please try again later.",
            life: 3000,
          });
          if (!error.response) {
            logger.warn("No error.response in response, rejecting.");
            return Promise.reject(error);
          }
        }
        // if (error.response.status === 503) { // FIXME:: this is super unsafe
        //   window.location.reload();
        // }
        throw error;
      }
    );
    return http;
  }

  createExcelExportNetwork() {
    const http = axios.create({
      baseURL: "/api",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      responseType: "blob",
    });
    http.interceptors.request.use(
      (config) => {
        let jwt = this.getJwt();
        if (!jwt || jwt === "") {
          throw "No JWT to use!";
        }
        config.headers[AUTH_HEADER] = jwt;
        config.headers[VERSION_HEADER] = this.getVersion();
        logger.debug("Added JWT to outgoing call.");
        return config;
      },
      (error) => {
        logger.error("AJAX call wasn't even sent.");
        logger.error(error);
        return Promise.reject(error);
      }
    );
    http.interceptors.response.use(
      (response) => {
        this.checkRefresh(response);
        let jwt = response.headers[AUTH_HEADER.toLowerCase()];
        if (jwt) {
          logger.debug("Refreshing JWT...");
          this.store.dispatch("session/refreshJwt", jwt);
        } else {
          logger.debug("No JWT in response!");
        }
        return Promise.resolve(response);
      },
      (error) => {
        logger.warn("AJAX call resulted in an error response.");
        logger.warn(error);
        if (error.response.status === 401) {
          this.store.dispatch("session/boot");
          this.router.push("/login");
          LoadingMixin.methods.doFinishLoading().then(() => {});
        }
        if (error.response.status === 403) {
          this.router.push("/");
        }
        if (error.response.status === 503) {
          window.location.reload();
        }
        return Promise.reject(error);
      }
    );
    return http;
  }

  createPdfExportNetwork() {
    const http = axios.create({
      baseURL: "/api",
      headers: {
        Accept: "application/pdf",
      },
      responseType: "blob",
    });
    http.interceptors.request.use(
      (config) => {
        let jwt = this.getJwt();
        if (!jwt || jwt === "") {
          throw "No JWT to use!";
        }
        config.headers[AUTH_HEADER] = jwt;
        config.headers[VERSION_HEADER] = this.getVersion();
        logger.debug("Added JWT to outgoing call.");
        return config;
      },
      (error) => {
        logger.error("AJAX call wasn't even sent.");
        logger.error(error);
        return Promise.reject(error);
      }
    );
    http.interceptors.response.use(
      (response) => {
        this.checkRefresh(response);
        let jwt = response.headers[AUTH_HEADER.toLowerCase()];
        if (jwt) {
          logger.debug("Refreshing JWT...");
          this.store.dispatch("session/refreshJwt", jwt);
        } else {
          logger.debug("No JWT in response!");
        }
        return Promise.resolve(response);
      },
      (error) => {
        logger.warn("AJAX call resulted in an error response.");
        logger.warn(error);
        if (error.response.status === 401) {
          this.store.dispatch("session/boot");
          this.store.dispatch("session/boot");
          this.router.push("/login");
          LoadingMixin.methods.doFinishLoading().then(() => {});
        }
        if (error.response.status === 403) {
          this.router.push("/");
        }
        if (error.response.status === 503) {
          window.location.reload();
        }
        return Promise.reject(error);
      }
    );
    return http;
  }

  async connection() {
    if (this.store.getters["session/isReady"]) return this.http;
    console.log("No JWT detected, establishing an interval to wait for it...");
    let rounds = 0;
    while (rounds < 6 && !this.store.getters["session/isReady"]) {
      console.log("Waiting for session establishment...");
      await delay(500);
      rounds++;
    }
    return this.http;
  }

  async excelConnection() {
    if (this.store.getters["session/isReady"]) return this.excelNetwork;
    console.log("No JWT detected, establishing an interval to wait for it...");
    let rounds = 0;
    while (rounds < 6 && !this.store.getters["session/isReady"]) {
      console.log("Waiting for session establishment...");
      await delay(500);
      rounds++;
    }
    return this.excelNetwork;
  }

  async pdfConnection() {
    if (this.store.getters["session/isReady"]) return this.pdfNetwork;
    console.log("No JWT detected, establishing an interval to wait for it...");
    let rounds = 0;
    while (rounds < 6 && !this.store.getters["session/isReady"]) {
      console.log("Waiting for session establishment...");
      await delay(500);
      rounds++;
    }
    return this.pdfNetwork;
  }

  checkRefresh(response) {
    const refreshHeader = response.headers["refresh"];

    if (refreshHeader) {
      const refreshTime = parseInt(refreshHeader, 10);

      if (!isNaN(refreshTime)) {
        console.log(
          `Received Refresh: ${refreshTime}, reloading page in ${refreshTime} seconds...`
        );

        setTimeout(() => {
          window.location.reload();
        }, refreshTime * 1000);
      }
    }
  }
}

// singleton
export default new Network();
